'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildPromoCountdown,
  EMPTY_PURCHASE_PROMO,
  type PromoCountdown,
  type PurchasePromo,
} from '@/utils/purchasePromo';

function promoBaseUrl(): string {
  return (process.env.NEXT_PUBLIC_BASE_URL || '').replace(/\/$/, '');
}

export function usePurchasePromo() {
  const [promo, setPromo] = useState<PurchasePromo>(EMPTY_PURCHASE_PROMO);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState<PromoCountdown>(buildPromoCountdown(null, false));

  const reload = useCallback(async () => {
    const baseUrl = promoBaseUrl();
    if (!baseUrl) {
      setPromo(EMPTY_PURCHASE_PROMO);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${baseUrl}/api/payment/promo`, { cache: 'no-store' });
      if (!response.ok) {
        setPromo(EMPTY_PURCHASE_PROMO);
        return;
      }
      const data = (await response.json()) as PurchasePromo;
      setPromo(data);
    } catch {
      setPromo(EMPTY_PURCHASE_PROMO);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const tick = () => {
      const next = buildPromoCountdown(promo.endsAt, promo.active);
      setCountdown(next);
      if (next.expired && promo.active) {
        void reload();
      }
    };

    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [promo.active, promo.endsAt, reload]);

  const promoActive = promo.active && !countdown.expired;
  const effectivePromo = useMemo<PurchasePromo>(
    () => ({ ...promo, active: promoActive }),
    [promo, promoActive],
  );

  return { promo: effectivePromo, loading, countdown, reload, promoActive };
}
